import React, { useEffect, useState } from "react";
import {
  formatPhoneNumber,
  displayPhoneNumber,
  cleanPhoneNumber,
} from "../../../utils/phoneUtils";
import { Icons } from "../Icons";

export interface InputProps {
  id?: string;
  name?: string;
  type?:
    | "text"
    | "email"
    | "tel"
    | "password"
    | "number"
    | "url"
    | "date"
    | "time";
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  label?: string;
  error?: string;
  min?: number;
  max?: number;
  step?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  /** Use on always-dark surfaces (e.g. login card), independent of admin color mode. */
  variant?: "default" | "onDark";
  leadingIcon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  id,
  name,
  type = "text",
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  placeholder,
  disabled = false,
  required = false,
  className = "",
  label,
  error,
  min,
  max,
  step,
  autoComplete,
  autoFocus = false,
  variant = "default",
  leadingIcon,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const isTimeOrDate = type === "time" || type === "date";
  const onDark = variant === "onDark";

  const baseClasses = onDark
    ? [
        "w-full min-w-0 max-w-full px-4 py-3 rounded-xl text-base",
        "bg-gray-700/90 text-gray-100 placeholder:text-gray-400",
        "shadow-none",
        "transition-[border-color,box-shadow,background-color,color] duration-200",
        "disabled:bg-gray-800 disabled:text-gray-500",
        "disabled:placeholder:text-gray-600 disabled:cursor-not-allowed",
        "focus:outline-none focus:ring-2 focus:ring-offset-0",
      ].join(" ")
    : [
        "w-full min-w-0 max-w-full px-4 py-3 rounded-xl text-base",
        "bg-white dark:bg-slate-900",
        "text-gray-900 dark:text-slate-100",
        "placeholder:text-gray-400 dark:placeholder:text-slate-500",
        "shadow-sm dark:shadow-none",
        "transition-[border-color,box-shadow,background-color,color] duration-200",
        "disabled:bg-gray-50 dark:disabled:bg-slate-800/80",
        "disabled:text-gray-500 dark:disabled:text-slate-500",
        "disabled:placeholder:text-gray-400 dark:disabled:placeholder:text-slate-600",
        "disabled:cursor-not-allowed disabled:shadow-none",
        "focus:outline-none focus:ring-2 focus:ring-offset-0",
      ].join(" ");

  const timeDateClasses = isTimeOrDate
    ? [
        "text-base block appearance-none",
        onDark ? "[color-scheme:dark]" : "dark:[color-scheme:dark]",
        "[&::-webkit-date-and-time-value]:min-w-0",
        "[&::-webkit-calendar-picker-indicator]:ml-auto",
        "[&::-webkit-calendar-picker-indicator]:cursor-pointer",
        "[&::-webkit-calendar-picker-indicator]:opacity-90",
        "hover:[&::-webkit-calendar-picker-indicator]:opacity-100",
        onDark
          ? "[&::-webkit-calendar-picker-indicator]:brightness-125"
          : "dark:[&::-webkit-calendar-picker-indicator]:brightness-125",
      ].join(" ")
    : "";

  const borderClasses = error
    ? onDark
      ? "border border-red-400 hover:border-red-300 focus:ring-red-400/50 focus:border-red-400"
      : [
          "border border-red-300 dark:border-red-500",
          "hover:border-red-400 dark:hover:border-red-400",
          "focus:ring-red-500/40 dark:focus:ring-red-400/40",
          "focus:border-red-500 dark:focus:border-red-400",
        ].join(" ")
    : onDark
      ? "border border-gray-500 hover:border-gray-400 focus:ring-sky-400/40 focus:border-sky-400"
      : [
          "border border-gray-300 dark:border-slate-600",
          "hover:border-gray-400 dark:hover:border-slate-500",
          "focus:ring-sky-500/40 dark:focus:ring-sky-400/40",
          "focus:border-sky-500 dark:focus:border-sky-400",
        ].join(" ");

  const paddingLeftClass = leadingIcon ? " pl-11" : "";
  const paddingRightClass = isPassword ? " pr-11" : "";
  const combinedClasses =
    `${baseClasses} ${timeDateClasses} ${borderClasses}${paddingLeftClass}${paddingRightClass} ${className}`.trim();
  const inputType = isPassword && showPassword ? "text" : type;

  const labelClasses = onDark
    ? "block text-sm font-medium text-gray-200 mb-2"
    : "block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2";

  const errorClasses = onDark
    ? "mt-2 text-sm text-red-300 flex items-center gap-2"
    : "mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-2";

  const toggleClasses = onDark
    ? "absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-100 hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-0 disabled:opacity-50 disabled:pointer-events-none"
    : "absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-0 disabled:opacity-50 disabled:pointer-events-none";

  // Format prefilled phone numbers when component mounts or value changes
  useEffect(() => {
    if (type === "tel" && value && typeof value === "string") {
      const cleanValue = cleanPhoneNumber(value);

      // Only format if the value is unformatted (no parentheses or dashes)
      if (
        cleanValue.length >= 10 &&
        !value.includes("(") &&
        !value.includes("-")
      ) {
        const formatted = displayPhoneNumber(value);
        if (formatted !== value) {
          const syntheticEvent = {
            target: { value: formatted },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }
    }
  }, [value, type, onChange]);

  // Handle phone number formatting for tel inputs during typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === "tel") {
      const formatted = formatPhoneNumber(e.target.value);
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: formatted,
        },
      };
      onChange(syntheticEvent);
    } else {
      onChange(e);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className="w-full min-w-0">
      {label && (
        <label htmlFor={id} className={labelClasses}>
          {label}
          {required && (
            <span
              className={
                onDark ? "text-red-400 ml-1" : "text-red-500 dark:text-red-400 ml-1"
              }
              aria-hidden
            >
              *
            </span>
          )}
        </label>
      )}
      <div className="relative min-w-0">
        {leadingIcon ? (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            {leadingIcon}
          </div>
        ) : null}
        <input
          id={id}
          name={name}
          type={inputType}
          value={value}
          onChange={handleInputChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          min={min}
          max={max}
          step={step}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          data-variant={variant}
          aria-invalid={error ? true : undefined}
          aria-describedby={error && id ? `${id}-error` : undefined}
          className={combinedClasses}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className={toggleClasses}
            title={showPassword ? "Ocultar senha" : "Mostrar senha"}
            tabIndex={-1}
            disabled={disabled}
          >
            {showPassword ? (
              <Icons.VisibilityOff sx={{ fontSize: 20 }} />
            ) : (
              <Icons.Visibility sx={{ fontSize: 20 }} />
            )}
          </button>
        )}
      </div>
      {error && (
        <p
          id={id ? `${id}-error` : undefined}
          className={errorClasses}
          role="alert"
        >
          <span aria-hidden>⚠️</span>
          {error}
        </p>
      )}
    </div>
  );
};
